import { appstr } from "../utils/appstr";
import { enumMatchSubs, kGroupingRule } from "../constants";
import { PopulateConcat } from "../utils/PopulateConcat";
import { PProcessConcat } from "../utils/PProcessConcat";
import { enumRulePairLogicCondition, QRulePair } from "./QRulePair";
import { QXMLObject } from "./QXMLObject";
import { Globals } from "./Globals";

import { StringToBool } from "../utils/StringToBool.js";
import { PositionFilterCondition } from "../utils/PositionFilterCondition";

/**
 * @typedef {import('./QChartBox').QChartBox} QChartBox
 * @typedef {import('./QRulePair').QRulePair} QRulePair
 * @typedef {import('./QXDoc').QXDoc} QXDoc
 */

export const enumRuleType = {
  complex: "Complex Rule",
  basic: "Basic Rule",
  always: "Always Apply"
};

export class QRule extends QXMLObject {
  constructor() {
    super();
    /**
     * @type {(null | QRulePair[])}
     */
    this.fRulePairList = null;
    this.fMatchSubs = null;
    this.fNoRule = false;
    this.fLogicalOperator = null;
    this.fChainLevels = 0;
    this.fIncludeFiltered = false;
    this.fComplexRule = null;
    this.fComplexDef = null;
    this.fConcatCache = null;
    this.fFormulaType = null;
    this.fRuleCriteriaType = null;
    this.fRulesLastUID = 1;
  }

  /**
   * @param {QChartBox[]} boxList
   * @param {QXDoc} theDoc
   * @param {string} [actionSet]
   * @returns {QChartBox[]}
   */
  GetMatchingBoxes(boxList, theDoc, actionSet) {
    var matchList = [];

    boxList.forEach(aBox => {
      const theDataObj = aBox.GetDataObj();
      const isInvalid = theDataObj == null || aBox.GetLayoutLock();
      if (isInvalid) {
        return;
      }

      if (this.MatchRule(theDataObj, theDoc, actionSet)) {
        matchList.push(aBox);
      }
    });

    return matchList;
  }

  /**
   * @param {QChartBox[]} matchedList
   * @param {QXDoc} docRef
   * @returns {QChartBox[]}
   */
  GetRelatedBoxes(matchedList, docRef) {
    const relativeBoxes = new Set(matchedList);
    const plm = docRef.GetPageLayoutMgr();

    for (const chartBox of matchedList) {
      const dbObj = chartBox.GetDataObj();

      if (dbObj.IsPositionObj()) {
        const holderObjs = dbObj.GetHolderList() ?? [];
        for (const holderObj of holderObjs) {
          const holderId = holderObj.GetObjectID();
          const pageNode = plm.FindNodeFromDataObjID(holderId);
          const holderBox = pageNode?.GetChartBox();
          if (!holderBox) {
            continue;
          }

          relativeBoxes.add(holderBox);
        }
      }

      if (dbObj.IsHolderObj()) {
        const parentPosition = dbObj?.GetParentPosition();
        const positionId = parentPosition?.GetObjectID();
        if (!positionId) {
          continue;
        }

        const pageNode = plm.FindNodeFromDataObjID(positionId);
        const positionBox = pageNode?.GetChartBox();
        if (!positionBox) {
          continue;
        }

        relativeBoxes.add(positionBox);
      }
    }

    return [...relativeBoxes];
  }

  GetApplyToManagers() {
    return true;
  }

  /** @param {number|boolean} [matchSubs] enumMatchSubs value */
  InitRule(matchSubs = false, chainLevels = 0, includeFiltered = false) {
    this.fRulePairList = [];
    this.fMatchSubs = matchSubs;
    this.fLogicalOperator = "";
    this.fChainLevels = chainLevels;
    this.fComplexRule = false;
    this.fComplexDef = "";
    this.fIncludeFiltered = includeFiltered;
    this.fNoRule = false;
    this.fApplyToManagers = true;

    this.SetXMLElementName("rule");
  }

  GetIsComplex() {
    return this.fComplexRule;
  }

  SetIsComplex(isComplex) {
    this.fComplexRule = isComplex;
  }

  SetNoRule(setting) {
    this.fNoRule = setting;
  }

  GetComplexDef() {
    return this.fComplexDef;
  }

  SetComplexDef(complexDef) {
    this.fComplexDef = complexDef;
  }

  SetMatchSubs(matchSubs) {
    this.fMatchSubs = matchSubs;
  }

  GetMatchSubs() {
    return this.fMatchSubs;
  }

  GetChainLevels() {
    return this.fChainLevels;
  }

  SetChainLevels(chainLevels) {
    this.fChainLevels = chainLevels;
  }

  GetIncludeFiltered() {
    return this.fIncludeFiltered;
  }

  SetIncludeFiltered(includeFiltered) {
    this.fIncludeFiltered = includeFiltered;
  }

  AddBlankRulePair(insertAt) {
    return this.AddRulePair("", "", "", "equals", "", enumRulePairLogicCondition.or, insertAt);
  }

  AddRulePair(fieldName, fieldID, fieldClassName, comparison, value, condition, insertAt) {
    var rulePair = new QRulePair();
    rulePair.InitRulePair(fieldName, fieldID, fieldClassName, comparison, value);
    rulePair.fCondition = condition || enumRulePairLogicCondition.or;
    if (insertAt !== undefined) {
      this.fRulePairList.splice(insertAt, 0, rulePair);
    } else {
      this.fRulePairList.push(rulePair);
    }
    return rulePair;
  }

  GetNumRulePairs() {
    return this.fRulePairList.length;
  }

  IsRuleEmpty() {
    if (this.GetIsComplex()) return this.fComplexDef.length == 0;
    else return this.GetNumRulePairs() == 0;
  }

  GetRulePairList() {
    return this.fRulePairList;
  }

  RemoveAllRulePairs() {
    this.fRulePairList = [];
  }

  GetIndexRulePair(index) {
    return this.fRulePairList[index];
  }

  RemoveIndexRulePair(theIndex) {
    this.fRulePairList.splice(theIndex, 1);
  }

  RemoveRulePair(theRulePair) {
    var index = this.fRulePairList.indexOf(theRulePair);
    if (index != -1) this.fRulePairList.splice(index, 1);
  }

  SetLogicalOperator(logicalOperator) {
    this.fLogicalOperator = logicalOperator;
  }

  GetLogicalOperator() {
    return this.fLogicalOperator;
  }

  MatchComplexRule(theObj, docRef) {
    var c = this.ProcessConcat(theObj, null, docRef, true, true);
    var v = c.indexOf("{");
    if (v != -1 || c == "") return false;

    var e = "";
    try {
      e = eval(c);
    } catch (err) {
      e = false;
    }
    return e;
  }

  IsLevelRule() {
    var isComplex = this.GetIsComplex();
    if (isComplex) return false;

    var n = this.GetNumRulePairs();
    if (n != 1) return false;

    var rp = this.GetIndexRulePair(0);
    var fieldID = rp.GetFieldID();

    if (fieldID != appstr("Level")) return false;

    return true;
  }

  IsNoRule() {
    return this.fNoRule;
  }

  SetRuleCriteria(theCriteria) {
    if (["Always Apply", "Basic Rule", "Complex Rule"].includes(theCriteria))
      this.fRuleCriteriaType = theCriteria;
  }

  MatchRule(theObj, docRef, ruleAction = undefined, resultOnClassMismatch = false) {
    // Manager exclusion for grouping is handled in QXDoc.ApplyChartGrouping's
    // isManagerBox filter, not here — _SpanOfControl was unreliable on mock docs.
    if (this.GetIsComplex()) return this.MatchComplexRule(theObj, docRef);

    const n = this.GetNumRulePairs();
    if (n == 0 || this.IsNoRule()) return true;

    const logicalOperator = this.GetLogicalOperator();

    // IsNullish from storybook library is not accessible from coreapp
    if (logicalOperator == null || logicalOperator === "") {
      return this.MatchOnRulePairs(theObj, docRef, resultOnClassMismatch);
    } else {
      return this.MatchOnLogicalOperator(theObj, docRef, resultOnClassMismatch, logicalOperator);
    }
  }

  MatchOnLogicalOperator(theObj, docRef, resultOnClassMismatch, logicalOperator) {
    if (logicalOperator === "and") {
      for (let rulePair of this.GetRulePairList()) {
        const gotMatch = rulePair.PairMatch(theObj, docRef, resultOnClassMismatch);
        if (!gotMatch) return false;
      }
      return true;
    } else if (logicalOperator === "or") {
      for (let rulePair of this.GetRulePairList()) {
        const gotMatch = rulePair.PairMatch(theObj, docRef, resultOnClassMismatch);
        if (gotMatch) return true;
      }
      return false;
    } else {
      return false;
    }
  }

  MatchOnRulePairs(theObj, docRef, resultOnClassMismatch) {
    let orBlocks = QRulePair.SplitIntoOrBlocks(this.GetRulePairList());

    for (let orBlock of orBlocks) {
      let blockMatched = false;

      // Invalid rules are skipped.
      let currentOrBlock = orBlock.filter(rp => rp.IsValid());
      if (currentOrBlock.length == 0) continue;

      // Check or conditions.
      for (let rulePair of currentOrBlock) {
        const gotMatch = rulePair.PairMatch(theObj, docRef, resultOnClassMismatch);
        if (gotMatch) {
          blockMatched = true;
          break;
        }
      }

      // Every block is connected by AND, so if one block fails, the whole rule fails.
      if (!blockMatched) return false;
    }

    return true;
  }

  PositionsMatchRule(theObj, docRef, ruleAction = undefined) {
    const applyToMgrs = this.GetApplyToManagers(ruleAction === kGroupingRule);

    if (!applyToMgrs && ruleAction == kGroupingRule) {
      const spanOfControlField =
        theObj.GetFieldDefByID("_SpanOfControl") || theObj.GetFieldDefByID("SpanOfControl");
      const isMgr = theObj.GetFieldValue(spanOfControlField);
      if (isMgr) return PositionFilterCondition.AllFalse(theObj);
    }

    // if (this.GetIsComplex()) return this.MatchComplexRule(theObj, docRef);

    const nRules = this.GetNumRulePairs();
    if (nRules == 0 || this.IsNoRule()) return PositionFilterCondition.AllTrue(theObj);

    let rp = this.GetIndexRulePair(0);
    const result = rp.PositionsPairMatch(theObj, docRef);
    if (nRules == 1) return result;

    const logicalOperator = this.GetLogicalOperator();

    rp = this.GetIndexRulePair(1);
    const result2 = rp.PositionsPairMatch(theObj, docRef);

    if (logicalOperator == "and") {
      return result.and(result2);
    } else if (logicalOperator == "or") {
      return result.or(result2);
    }
  }

  UpdateComplexRuleReferences(origName, newName) {
    // ComplexRuleToDo
  }

  GetRuleString(docRef) {
    //TODO: Rule criteria type handling like this is mostly for chart template rules, but we'll roll it into all rule types when we can.
    var criteriaType = this.fRuleCriteriaType;
    var criteriaString = appstr("Always Apply");

    if (criteriaType == "Basic Rule") {
      var firstPair = this.GetIndexRulePair(0);
      criteriaString = firstPair.GetRulePairString(docRef);
      if (this.GetNumRulePairs() == 2) {
        var secondPair = this.GetIndexRulePair(1);
        criteriaString +=
          " " + appstr(this.GetLogicalOperator()) + " " + secondPair.GetRulePairString(docRef);
      }
    } else if (criteriaType == "Complex Rule") {
      criteriaString = this.GetComplexDef();
    }

    return criteriaString;
  }

  DoClone(cloneFrom) {}

  NewObject() {
    return new QRule();
  }

  Clone() {
    var c = this.NewObject();
    c.InitRule(this.fMatchSubs, this.fChainLevels, this.fIncludeFiltered);

    c.fComplexRule = this.fComplexRule;
    c.fComplexDef = this.fComplexDef;
    c.fRulesLastUID = this.fRulesLastUID;
    c.fRuleCriteriaType = this.fRuleCriteriaType;

    var n = this.GetNumRulePairs();
    for (var i = 0; i < n; i++) {
      var rp = this.GetIndexRulePair(i);
      var clonePair = rp.Clone();
      c.fRulePairList.push(clonePair);
    }
    c.fLogicalOperator = this.fLogicalOperator;

    c.DoClone(this);

    return c;
  }

  DoRulesMatch(compareTo) {
    return true;
  }

  RulesMatch(compareTo) {
    if (this.fMatchSubs !== compareTo.fMatchSubs) return false;
    if (this.fChainLevels !== compareTo.fChainLevels) return false;
    if (this.fIncludeFiltered !== compareTo.fIncludeFiltered) return false;

    var isComplex = this.GetIsComplex();
    var isNoRule = this.IsNoRule();
    if (isComplex !== compareTo.GetIsComplex() || isNoRule !== compareTo.IsNoRule()) return false;

    if (isComplex) {
      if (this.fComplexDef !== compareTo.fComplexDef) return false;
    } else {
      if (this.fLogicalOperator !== compareTo.fLogicalOperator) return false;
      var n = this.GetNumRulePairs();
      var n1 = compareTo.GetNumRulePairs();
      if (n != n1) return false;

      for (var i = 0; i < n; i++) {
        var rp = this.GetIndexRulePair(i);
        var rp1 = compareTo.GetIndexRulePair(i);
        if (rp.RulePairsMatch(rp1) == false) return false;
      }
    }

    return this.DoRulesMatch(compareTo);
  }

  ValidateRule(ignoreDoValidate) {
    var r = this;
    var isComplex = r.GetIsComplex();
    if (isComplex) {
      var newRule = r.fComplexDef;
      if (newRule == null || newRule === "") return appstr("No Rule");
      newRule = newRule.replaceAll("&&", "&");
      newRule = newRule.replaceAll("||", "|");

      var docRef = Globals._wsMgr.GetDoc();
      var objDB = docRef.GetObjDB();
      var boxClass = objDB.GetClassByName("Box");
      var dbObj = boxClass.GetIndexObject(0);

      var concatCache = [];
      PProcessConcat(concatCache, newRule, dbObj, null, docRef);

      if (this.fFormulaType === "concatenate") {
        concatCache = this.GetConcatCache(concatCache);
      }

      var c = PopulateConcat(concatCache, dbObj, null, docRef, true, true);

      try {
        var e = eval(c);
      } catch (err) {
        return err.message;
      }
    } else if (this.fRuleCriteriaType != "Always Apply") {
      var l = this.GetLogicalOperator();
      var n = r.GetNumRulePairs();

      if (l == "" && n == 2)
        //Intentionally written this way because we don't handle more than 2 total pairs
        r.RemoveIndexRulePair(1);

      n = r.GetNumRulePairs();
      for (var i = 0; i < n; i++) {
        var rp = r.GetIndexRulePair(i);
        if (!rp.IsValid()) return appstr("fieldcompblank");
      }
    }

    if (ignoreDoValidate) return "success";

    return this.DoValidateRule();
  }

  GetConcatCache(concatCache) {
    const operators = ["|", "&", "=", "==", ">=", "<="];
    let newString = [];
    for (let i = 0; i < concatCache.length; i++) {
      if (concatCache[i] === " ") newString.push("+");
      else newString.push(concatCache[i]);
      if (
        (i < concatCache.length - 1 &&
          concatCache[i].includes(operators) &&
          concatCache[i + 1].includes(operators)) ||
        i + 1 >= concatCache.length ||
        concatCache[i].length == 1 ||
        concatCache[i + 1].length == 1
      ) {
        continue;
      } else {
        newString.push("+");
      }
    }
    concatCache = newString;
    return concatCache;
  }

  DoValidateRule() {
    return "success";
  }

  DoUpdateFrom(updateFrom) {}

  UpdateFrom(updateFrom) {
    this.fMatchSubs = updateFrom.fMatchSubs;
    this.fChainLevels = updateFrom.fChainLevels;
    this.fIncludedFiltered = updateFrom.fIncludedFiltered;

    this.fLogicalOperator = updateFrom.fLogicalOperator;
    this.fComplexRule = updateFrom.fComplexRule;
    this.fComplexDef = updateFrom.fComplexDef;

    this.fNoRule = updateFrom.fNoRule;

    var n1 = updateFrom.GetNumRulePairs();
    this.fRulePairList = [];
    for (var i = 0; i < n1; i++) {
      var rp = updateFrom.GetIndexRulePair(i);
      this.AddRulePair(
        rp.GetFieldName(),
        rp.GetFieldID(),
        rp.GetFieldClassName(),
        rp.GetComparison(),
        rp.GetValue()
      );
    }

    this.DoUpdateFrom(updateFrom);
  }

  DoOutputSubElements(stream) {}

  OutputSubElements(stream) {
    this.OutputSimpleElement("matchsubs", this.fMatchSubs, stream);
    this.OutputSimpleElement("chainlevels", this.fChainLevels, stream);
    this.OutputSimpleElement("includef", this.fIncludedFiltered, stream);

    this.OutputSimpleElement("logicalop", this.fLogicalOperator, stream);
    this.OutputSimpleElement("complex", this.fComplexRule, stream);
    this.OutputSimpleElement("complexdef", this.fComplexDef, stream);

    this.DoOutputSubElements(stream);
  }

  OutputChildElements(stream) {
    var n = this.GetNumRulePairs();
    for (var i = 0; i < n; i++) {
      var rp = this.GetIndexRulePair(i);
      rp.OutputToXML(stream);
    }
  }

  OutputToJSON(json) {
    //Cross reference QChartTemplateRule.OutputRuleToJSON for a similar but not identical implementation.
    var ruleJSON = {};

    //TODO: Embrace the 'type' flag used already in chart template rule. More manageable data structure.
    ruleJSON["matchSubs"] = this.fMatchSubs;
    ruleJSON["chainLevels"] = this.fChainLevels;
    ruleJSON["includeFiltered"] = this.fIncludeFiltered;
    if (this.fRulePairList) {
      ruleJSON["rulePairs"] = this.fRulePairList.map(pair => {
        return pair.OutputToJSON();
      });
    }

    ruleJSON["noRule"] = this.fNoRule;
    ruleJSON["applyToManagers"] = this.fApplyToManagers;
    ruleJSON["logicalOperator"] = this.fLogicalOperator;
    ruleJSON["isComplex"] = this.fComplexRule;
    ruleJSON["complexDefinition"] = this.fComplexDef;
    ruleJSON["ruleCriteriaType"] = this.fRuleCriteriaType;
    ruleJSON["lastUID"] = this.fRulesLastUID;

    if (json) json["rule"] = ruleJSON;
    else return ruleJSON;
  }

  LoadFromJSON(json) {
    this.fMatchSubs = json["matchSubs"];
    this.fChainLevels = json["chainLevels"];
    this.fIncludeFiltered = json["includeFiltered"];
    this.fNoRule = json["noRule"];
    this.fApplyToManagers = json["applyToManagers"];
    this.fRulesLastUID = json["lastUID"] || 1;
    if (json["rulePairs"]) {
      this.fRulePairList = json["rulePairs"].map(jsonRule => {
        const pair = new QRulePair();
        pair.LoadFromJSON(jsonRule);
        return pair;
      });
    }

    this.fLogicalOperator = json["logicalOperator"];
    this.fComplexRule = json["isComplex"];
    this.fComplexDef = json["complexDefinition"];
    this.fRuleCriteriaType = json["ruleCriteriaType"];

    return this;
  }

  DoLoadSubElements(objTree) {}

  LoadSubElements(objTree) {
    var v = objTree.GetValueForChild("matchsubs", false);

    if (v == "true") v = enumMatchSubs.directonly;
    else if (v == "false") v = enumMatchSubs.matchonly;
    else v = parseInt(v);

    var v2 = objTree.GetValueForChild("chainlevels", true);
    if (v2 == null) v2 = 0;

    var v3 = objTree.GetValueForChild("complex", false);
    var v4 = objTree.GetValueForChild("complexdef", false);
    if (v3 == null) v3 = "false";
    if (v4 == null) v4 = "";

    var v5 = objTree.GetValueForChild("includef", false);
    if (v5 == null) v5 = "false";

    this.fComplexRule = StringToBool(v3);
    this.fComplexDef = v4;

    this.fMatchSubs = v;
    this.fChainLevels = v2;
    this.fIncludeFiltered = StringToBool(v5);
    this.fLogicalOperator = objTree.GetValueForChild("logicalop", false);
    this.DoLoadSubElements(objTree);
  }

  LoadChildElements(objTree) {
    var childList = objTree.GetChildOfTypeList("pair");

    var numPairs = childList.length; // objTree.GetNumChildrenOfType("pair");
    for (var i = 0; i < numPairs; i++) {
      var rpInfo = childList[i];
      //var rpInfo = objTree.GetIndexChildOfType("pair",i);
      var rp = new QRulePair();
      rp.DoInit();
      rp.LoadFromXML(rpInfo);
      this.fRulePairList.push(rp);
    }
  }

  GetConcatFormula() {
    return this.fComplexDef;
  }

  ProcessConcat(dbObj, curDef, docRef, quoteValues, returnNumber) {
    if (this.fConcatCache == null) {
      var s = this.GetConcatFormula();
      if (s == null) return "";

      this.fConcatCache = [];
      PProcessConcat(this.fConcatCache, s, dbObj, curDef, docRef);
    }

    // return this.PopulateConcat(dbObj,curDef,docRef,quoteValues);
    return PopulateConcat(this.fConcatCache, dbObj, curDef, docRef, quoteValues, returnNumber);
  }

  GetUIDAndIncrement() {
    return this.fRulesLastUID++;
  }

  EnsureRulesUIDs() {
    this.fRulePairList?.forEach(rule => {
      if (!rule.fUID) rule.fUID = this.GetUIDAndIncrement();
    });
  }
}
